import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  BaseCssProperties,
  BaseWidgetProperties,
  FieldAttribute,
  LayoutWidgetConfig,
  ProviderClass,
  UiZoneWidgetConfig,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { ConfigService } from '../../../../../lib/services/config-service/config-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../../lib/services/multipart-holder/multipart-form-holder.service';

import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { debounceTime, map, takeUntil } from 'rxjs';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-password',
  templateUrl: './password.component.html',
  styleUrls: ['./password.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PasswordComponent extends FormSubmitter implements OnInit {
  @Output() formValuesChange = new EventEmitter(); // this emitter is used for passing value from this component to parent component
  @Input() formActionStateData: { [key: string]: string }; // to get form action state value from parent component
  @Input() columnStyle = false;
  @Input() dynamicZone = false;
  @Input() fieldAttributes: FieldAttribute[]; // to get field attributes from parent component which is different from this component

  public fieldAttributesPassword: FieldAttribute[];
  zone = 'onboarding';
  widgetConfig: UiZoneWidgetConfig;
  public totalSteps: number;
  passwordForm: FormGroup;
  isStepComplete = false;
  isError = false;
  widgetsAttributes: LayoutWidgetConfig[] = [];
  passwordString = '';
  urlParams: string[] = [];
  isShowErrorMsg = false;
  errorMsg = '';
  currentWidgetPosition: number;
  borderStyle: baseCssConfigStyle;
  nextPageUrl: any;
  passwordFormValue = { password: '' };
  colorTextAlignStyle: baseCssConfigStyle;
  colorStyle: baseCssConfigStyle;
  public isPassShowSuccessIcon = false;
  public isPassShowErrorIcon = false;
  public textInputFontStyle: baseCssConfigStyle;
  public textInputBackgroundColor: baseCssConfigStyle;

  public inputStyle: baseCssConfigStyle;
  public inputBaseStyle: baseCssConfigStyle;
  public inputInactiveStyle: baseCssConfigStyle;
  public currentInputStyle: baseCssConfigStyle;
  private isValueChanged = false;
  public isShowPasswordRule = true;
  public renderedPasswordRulesTemplate = ``;
  public passwordInputFieldsStyle: { [key: string]: baseCssConfigStyle } = {};

  constructor(
    private fbuilder: FormBuilder,
    private configService: ConfigService,
    private formHolderService: MultiPartFormHolderService,
    private urlZoneInput: ZonePageUrlProvider,
    public dataIn: ProviderClass<any>,
    protected override store: Store,
    private formSubmissionService: FormSubmissionService,
    public pageConfig: PageConfigurationProvider,
    private alertHandlerService: AlertHandlerService,
  ) {
    super(store);
    this.zone = this.urlZoneInput.zoneUrlInput;
    if (this.dataIn != undefined && this.dataIn != null) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
      this.fieldAttributesPassword = this.widgetConfig?.attributes['field_attributes'];
      this.isShowPasswordRule = this.attri?.['isShowPasswordRule'] ?? this.isShowPasswordRule;
    }
    this.renderedPasswordRulesTemplate = Handlebars.compile(this.getPasswordRulesText)({});
  }

  public get getPasswordRulesText(): string {
    if (this.attri?.['passwordRules'] != null) return this.attri?.['passwordRules'];
    else return ``;
  }

  ngOnInit(): void {
    if (this.dynamicZone && this.fieldAttributes) {
      this.fieldAttributesPassword = this.fieldAttributes;
    }
    const formStage = new FormStage({
      name: WidgetNames.PASSWORD_WIDGET,
      position: 0,
      route: '',
      form: new FormGroup({
        password: new FormControl(
          '',
          Validators.compose([
            Validators.required,
            patternValidator(/\d/, { hasNumber: true }),
            patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            patternValidator(/[a-z]/, { hasSmallCase: true }),
            patternValidator(/[^\p{L}\p{N}]/u, {
              hasSpecialCharacters: true,
            }),
            Validators.minLength(8),
          ]),
        ),
        confirm_password: new FormControl('', Validators.compose([Validators.required])),
      }),
    });

    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);

    this.getOnboardingZoneLayoutConfig();

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
    ]);

    this.borderStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.BORDER,
      BaseCssProperties.BACKGROUND_COLOR,
    ]);
    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
    ]);
    this.colorStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.COLOR]);
    this.textInputFontStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.TEXT_INPUT_FONT_SIZE,
    ]);
    this.subscribeToFormAction().subscribe((response) => {
      if (response?.nextPageUrl) this.nextPageUrl = response.nextPageUrl;
      if (response?.type === 'submitStep') this.onSubmit();
      if (response?.formData?.['password']) this.patchFormValues(response.formData);
    });
    this.getAndAssignInputFieldStyles(this.attri.widgetProperties);
    this.checkPasswordSuccessErrorIconFlag();

    const { baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.attri.widgetProperties,
    );

    this.inputStyle = baseStyle;
    this.inputBaseStyle = activeStyle;
    this.inputInactiveStyle = inactiveStyle;
    this.currentInputStyle = {
      ...this.inputInactiveStyle,
      ...this.inputBaseStyle,
    };
  }
  private checkPasswordSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isPassShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isPassShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }
  getOnboardingZoneLayoutConfig() {
    this.buildForm();
  }

  private getAndAssignInputFieldStyles(widgetProperties: BaseWidgetProperties) {
    const { baseStyle, activeStyle, inactiveStyle } =
      this.configService.getInputFieldCssStyles(widgetProperties);

    this.inputStyle = baseStyle;
    this.inputBaseStyle = activeStyle;
    this.inputInactiveStyle = inactiveStyle;
    this.widgetConfig.attributes?.['field_attributes'].forEach((field: any) => {
      this.passwordInputFieldsStyle[field.name] = {
        ...this.inputInactiveStyle,
        ...this.inputBaseStyle,
      };
    });
  }

  buildForm() {
    const stage = this.formHolderService.config.getStage(WidgetNames.PASSWORD_WIDGET);
    this.passwordForm = stage.getForm();
    this.passwordForm.addValidators([passwordMatchValidator]);

    this.passwordForm.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        map((formValues) => {
          const errors = this.passwordForm.errors;
          this.isError = errors ? !!errors?.['noPassswordMatch'] : false;
          if (this.isValueChanged) {
            this.formValuesChange.emit(formValues);
            this.isValueChanged = false;
          }
        }),
      )
      .subscribe();

    /*
      This dynamicZone and formActionStateData are used for getting data from parent component
      to setting up the initial value.
     */
    if (this.dynamicZone && this.formActionStateData !== undefined) {
      this.patchFormValues(this.formActionStateData);
    }
  }

  public valueChangesTrigger() {
    this.isValueChanged = true;
  }

  patchFormValues(formData: Record<string, string>) {
    this.passwordForm.patchValue({
      password: formData?.['password'],
      confirm_password: formData?.['confirm_password'],
    });
    this.passwordFormValue.password = formData?.['password'];
    this.passwordString = this.passwordForm.value?.password?.replace(/./g, '*');
    this.isStepComplete = true;
    this.isError = false;
  }

  onSubmit() {
    if (this.passwordForm.valid) {
      const formValues = this.passwordForm.value;
      this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then((success) => {
        if (success) {
          this.passwordString = this.passwordForm.value?.password?.replace(/./g, '*');
          this.isStepComplete = true;
          this.isError = false;
          // Form submission was successful, you can handle it here
        } else {
          // Handle submission failure
        }
      });
      this.isStepComplete = true;
      this.isError = false;
    } else {
      this.passwordForm.markAllAsTouched();
      this.isStepComplete = false;
      this.isError = true;
      this.errorMsg = 'Passwords do not match';
      if (this.passwordForm.hasError('noPassswordMatch')) {
        this.passwordMismatchErrorAlert(this.errorMsg);
      }
    }
  }

  private passwordMismatchErrorAlert(errorMsg: string) {
    this.alertHandlerService.showAlertFn('error', errorMsg);
  }

  showErrorMsg() {
    this.isShowErrorMsg = !this.isShowErrorMsg;
  }

  changePassType(field: FieldAttribute) {
    field.type = field.type == 'text' ? 'password' : 'text';
  }

  public updateFieldStyles(event: boolean, fieldName: string) {
    if (event) {
      this.passwordInputFieldsStyle[fieldName] = {
        ...this.inputBaseStyle,
        ...this.inputStyle,
      };
    } else {
      this.passwordInputFieldsStyle[fieldName] = {
        ...this.inputBaseStyle,
        ...this.inputInactiveStyle,
      };
    }
  }
}

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password: string = control.get('password')?.value; // get password from our password form control
  const confirmPassword: string = control.get('confirm_password')?.value; // get password from our confirmPassword form control
  return password === confirmPassword ? null : { noPassswordMatch: true };
};

export function patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // test the value of the control against the regexp supplied
    const valid = regex.test(control.value);

    // if true, return no error (no error), else return error passed in the second parameter
    return valid ? null : error;
  };
}
